// components/AsyncAutocomplete.js
import React, { useState, useEffect, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

export default function AsyncAutocomplete({
    label = "Search",
    placeholder = "Type to search...",
    value,
    onChange,
    fetchOptions, // (searchQ) => Promise<array>
    getOptionLabel = (option) => option?.label || "",
    isOptionEqualToValue,
    renderOption,
    debounceTime = 500,
    prefetch = false,
    prefetchDependency = [],
    ...props
}) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");

    // debounce util
    const debounce = (func, delay) => {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => func(...args), delay);
        };
    };

    useEffect(() => {
        prefetch && loadOptions("")
    }, [...prefetchDependency]);

    const loadOptions = async (q) => {
        try {
            setLoading(true);
            const data = await fetchOptions(q);
            setOptions(data || []);
        } catch (err) {
            console.error("AsyncAutocomplete fetch failed", err);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const debouncedFetch = useMemo(
        () => debounce(loadOptions, debounceTime),
        [fetchOptions]
    );

    useEffect(() => {
        if (searchText) {
            debouncedFetch(searchText);
        } else {
            setOptions([]);
        }
    }, [searchText]);

    return (
        <Autocomplete
            fullWidth
            loading={loading}
            options={options}
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            onInputChange={(_, newInput) => setSearchText(newInput)}
            getOptionLabel={getOptionLabel}
            {...(isOptionEqualToValue && { isOptionEqualToValue })}
            renderOption={renderOption}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={placeholder}
                    size="small"
                />
            )}
            {...props}
        />
    );
}
